import { useCallback, useEffect, useRef, useState } from 'react'

import { validateServiceMap } from '@/api/processing/services'
import { ServiceMapError } from '@/api/processing/types'
import { tusUploadFile } from '@/utils/tusUpload'

export type ServiceMapStatus =
  | 'idle'
  | 'uploading'
  | 'checking'
  | 'valid'
  | 'invalid'
  | 'error'

export interface UseServiceMapValidationParams {
  file: File | null
  userId?: number
}

interface UploadRecord {
  file: File
  settled: boolean
  promise: Promise<string>
}

export interface UseServiceMapValidationResult {
  status: ServiceMapStatus
  errors: ServiceMapError[]
  uploadId: string | null
  // O arquivo a que `status`/`uploadId` se referem. O modal compara com o
  // campo: logo depois de uma troca, o `valid` ainda é do arquivo anterior.
  validatedFile: File | null
  retry: () => void
  reset: () => void
}

/**
 * PSV-424 — confere o Mapa de Atendimento assim que ele é anexado.
 *
 * Mesmo esqueleto do `useSelectionConsistency` (upload antecipado para o
 * anexo, promessa do upload guardada, resposta antiga descartada), com duas
 * diferenças de propósito:
 *
 * - `error` **bloqueia**. Na escolha ele libera porque o servidor refaz a
 *   conferência e o 400 aponta o problema; aqui o 400 da criação chegaria
 *   como toast genérico, e o usuário não saberia o que corrigir no mapa.
 * - um 400/404 da conferência também invalida o upload guardado: o id não
 *   tem `ProcessingUpload` no servidor, e repetir com ele não adianta — o
 *   retry reenvia o arquivo.
 */
const useServiceMapValidation = ({
  file,
  userId,
}: UseServiceMapValidationParams): UseServiceMapValidationResult => {
  const [status, setStatus] = useState<ServiceMapStatus>('idle')
  const [errors, setErrors] = useState<ServiceMapError[]>([])
  const [uploadId, setUploadId] = useState<string | null>(null)
  const [validatedFile, setValidatedFile] = useState<File | null>(null)
  const [attempt, setAttempt] = useState(0)

  const inFlightRef = useRef<UploadRecord | null>(null)
  const requestSeqRef = useRef(0)

  // Idempotente: o efeito roda a cada mudança de identidade do `File`.
  const goIdle = useCallback(() => {
    setValidatedFile(previous => (previous === null ? previous : null))
    setErrors(previous => (previous.length === 0 ? previous : []))
    setStatus(previous => (previous === 'idle' ? previous : 'idle'))
  }, [])

  const reset = useCallback(() => {
    requestSeqRef.current += 1
    inFlightRef.current = null
    setUploadId(previous => (previous === null ? previous : null))
    goIdle()
  }, [goIdle])

  const retry = useCallback(() => {
    setAttempt(previous => previous + 1)
  }, [])

  useEffect(() => {
    // Falsy, e não só `null`: remover o arquivo no `UploadFile` grava
    // `undefined`.
    if (!file) {
      reset()
      return
    }

    if (!userId) {
      goIdle()
      return
    }

    requestSeqRef.current += 1
    const seq = requestSeqRef.current
    let cancelled = false
    const isCurrent = () => !cancelled && requestSeqRef.current === seq

    const run = async () => {
      let uploadFailed = false

      try {
        if (inFlightRef.current?.file !== file) {
          // O id antigo aponta para o arquivo anterior.
          setUploadId(null)
          setValidatedFile(null)

          const record = { file, settled: false } as UploadRecord
          record.promise = tusUploadFile(file, userId, () => {})
          record.promise.then(
            () => {
              record.settled = true
            },
            () => {
              record.settled = true
            }
          )

          inFlightRef.current = record
        }

        if (!inFlightRef.current.settled) {
          setStatus('uploading')
        }

        const currentUploadId = await inFlightRef.current.promise.catch(
          error => {
            uploadFailed = true
            throw error
          }
        )

        if (!isCurrent()) return

        setUploadId(currentUploadId)
        setStatus('checking')

        const result = await validateServiceMap({
          serviceMapUploadId: currentUploadId,
        })

        if (!isCurrent()) return

        setValidatedFile(file)
        setErrors(result.errors ?? [])
        setStatus(result.valid ? 'valid' : 'invalid')
      } catch (error) {
        const httpStatus = (error as { response?: { status?: number } })
          ?.response?.status
        const uploadUnusable =
          uploadFailed || httpStatus === 400 || httpStatus === 404

        if (uploadUnusable && inFlightRef.current?.file === file) {
          inFlightRef.current = null
        }

        if (!isCurrent()) return

        setValidatedFile(null)
        setErrors([])
        setStatus('error')
      }
    }

    run()

    return () => {
      cancelled = true
    }
  }, [file, userId, attempt, reset, goIdle])

  return { status, errors, uploadId, validatedFile, retry, reset }
}

export default useServiceMapValidation
